import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  TextareaAutosize,
  Typography,
  styled,
} from "@mui/material";
import { AppSelect, InputBase, InputLabel } from "components/common/Input";
import { useEffect, useRef, useState } from "react";
import useUpdatePickup from "hooks/pickups/useUpdatePickup";
import usePickup from "hooks/pickups/usePickup";
import theme from "lib/theme";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_MONITOR_DETAILS,
  MONITOR_SIZE_OPTIONS,
  MONITOR_STAND_OPTIONS,
  MonitorDetails,
  PICKUP_ITEM_CATEGORIES,
  buildEstimatedItems,
  createEmptyQuantities,
  getCategoryItemCount,
  parseEstimatedItems,
} from "./pickupItemCategories";
import { useMissingRequirement } from "./requirementError";

interface PickupItemsDetailsAccordionProps {
  step: number;
  beforeSubmit?: () => Promise<boolean>;
}

const CategoryAccordion = styled(Accordion)({
  margin: "0 !important",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px !important",
  boxShadow: "none",
  "&:not(:last-child)": {
    marginBottom: "8px !important",
  },
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "0 0 8px 0 !important",
  },
});

const CategoryAccordionSummary = styled(AccordionSummary)({
  minHeight: "56px !important",
  padding: "0 16px !important",
  ".MuiAccordionSummary-content": {
    margin: "12px 0 !important",
    alignItems: "center",
  },
});

const Textarea = styled(TextareaAutosize)({
  "&": {
    padding: "10px",
    fontFamily: "'Catamaran', sans-serif",
    fontSize: "16px",
    resize: "none",
    borderColor: "rgb(206, 212, 218)",
    borderRadius: "5px",
    width: "100%",
  },
  "&:focus, &:focus-visible": { borderColor: "rgb(0, 137, 189)", outlineColor: "rgb(0, 137, 189)" },
  "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
});

const quantityInputProps = {
  min: 0,
  onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.target instanceof HTMLElement && e.target.blur(),
};

export default function PickupItemsDetailsAccordion({ step, beforeSubmit }: PickupItemsDetailsAccordionProps) {
  const [quantities, setQuantities] = useState(createEmptyQuantities);
  const [otherDescription, setOtherDescription] = useState("");
  const [additionalItems, setAdditionalItems] = useState("");
  const [monitorDetails, setMonitorDetails] = useState<MonitorDetails>(DEFAULT_MONITOR_DETAILS);
  const [initialStateLoaded, setInitialStateLoaded] = useState(false);
  const isSubmittingRef = useRef(false);
  const { updatePickup } = useUpdatePickup();
  const { pickup } = usePickup();
  const navigate = useNavigate();
  const breakdownMissing =
    useMissingRequirement("pickup-estimated-items-form") &&
    !Object.values(quantities).some((value) => Number(value) > 0) &&
    !otherDescription.trim() &&
    !additionalItems.trim();

  useEffect(() => {
    if (pickup) {
      const parsed = parseEstimatedItems(
        pickup.pickupEstimatedItems.map(({ name, quantity, description }) => ({
          name,
          quantity,
          description,
        }))
      );

      setQuantities(parsed.quantities);
      setOtherDescription(parsed.otherDescription);
      setAdditionalItems(parsed.additionalItems);
      setMonitorDetails(parsed.monitorDetails);
      setInitialStateLoaded(true);
    }
  }, [pickup]);

  useEffect(() => {
    if (initialStateLoaded) {
      updatePickup({
        estimatedItems: buildEstimatedItems(quantities, otherDescription, additionalItems, monitorDetails),
      });
    }
  }, [initialStateLoaded, quantities, otherDescription, additionalItems, monitorDetails]);

  const handleQuantityChange = (name: string, value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) {
      return;
    }

    setQuantities((current) => ({ ...current, [name]: value }));
  };

  const handleMonitorDetailChange = (field: keyof MonitorDetails, value: string) => {
    setMonitorDetails((current) => ({ ...current, [field]: value }));
  };

  const renderCategoryBadge = (categoryKey: string, itemCount: number) => {
    if (categoryKey === "additional-items") {
      return (
        <Chip
          label="Optional"
          size="small"
          sx={{
            backgroundColor: "#F5F5F5",
            color: theme.palette.text.secondary,
            fontWeight: 600,
          }}
        />
      );
    }

    return (
      <Chip
        label={itemCount > 0 ? `${itemCount} items` : "None"}
        size="small"
        sx={{
          backgroundColor: itemCount > 0 ? theme.palette.lightBlue.main : "#F5F5F5",
          color: itemCount > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
          fontWeight: 600,
        }}
      />
    );
  };

  return (
    <form
      id="pickup-estimated-items-form"
      onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isSubmittingRef.current) {
          return;
        }

        isSubmittingRef.current = true;

        const saved = await beforeSubmit?.();
        if (saved === false) {
          isSubmittingRef.current = false;
          return;
        }

        if (pickup?.state === "missing_info") {
          navigate(`/pickups/${pickup.uuid}/summary`);
        } else {
          navigate("/pickups");
        }
      }}
    >
      <Box>
        <Typography variant="h6" fontSize="18px" fontWeight="bold">
          {step}. What’s in your pickup?
        </Typography>
        <Typography mt={0.5} variant="body2">
          Let’s breakdown what’s in your pickup. If you’re not exactly sure, take your best guess.
        </Typography>
        {breakdownMissing && <FormHelperText error>Enter at least 1 item</FormHelperText>}

        <Box mt={3}>
          {PICKUP_ITEM_CATEGORIES.map((category) => {
            const Icon = category.icon;
            const itemCount = getCategoryItemCount(category, quantities);

            return (
              <CategoryAccordion key={category.key} defaultExpanded={false} disableGutters>
                <CategoryAccordionSummary expandIcon={<ExpandMore color="primary" />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Icon color="primary" sx={{ fontSize: 20 }} />
                    <Typography fontWeight={600} sx={{ flex: 1 }}>
                      {category.title}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>{renderCategoryBadge(category.key, itemCount)}</Box>
                  </Box>
                </CategoryAccordionSummary>

                <AccordionDetails
                  sx={{ px: 2, pb: 1, pt: 0, backgroundColor: "#FAFBFD", borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}
                >
                  {category.key === "additional-items" ? (
                    <FormControl variant="standard" sx={{ width: "100%", p: 2 }}>
                      <InputLabel shrink>Anything we missed? Add it here.</InputLabel>
                      <Textarea
                        id="pickups-additional-items"
                        minRows={4}
                        value={additionalItems}
                        placeholder="e.g. label printers, badge readers, point-of-sale terminals..."
                        onChange={(e) => setAdditionalItems(e.target.value)}
                      />
                    </FormControl>
                  ) : (
                    <Box>
                      {category.items.map((item, index) => {
                        const isLastItem = index === category.items.length - 1;
                        const itemBorderSx = isLastItem ? {} : { borderBottom: "1px dashed rgba(0, 0, 0, 0.08)" };

                        if (item.isTextField) {
                          return (
                            <Box
                              key={item.name}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                py: 1,
                                ...itemBorderSx,
                              }}
                            >
                              <Typography sx={{ flex: 1 }}>{item.label}</Typography>
                              <InputBase
                                id="pickups-other-description"
                                value={otherDescription}
                                placeholder="Loose cables, batteries, etc."
                                onChange={(e) => setOtherDescription(e.target.value)}
                                sx={{ width: { xs: "100%", sm: 280 } }}
                              />
                            </Box>
                          );
                        }

                        return (
                          <Box key={item.name} sx={itemBorderSx}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                py: 1,
                              }}
                            >
                              <Typography sx={{ flex: 1 }}>{item.label}</Typography>
                              <InputBase
                                id={`pickups-quantity-${item.name}`}
                                type="number"
                                value={quantities[item.name]}
                                placeholder="0"
                                onChange={(e) => handleQuantityChange(item.name, e.target.value)}
                                inputProps={quantityInputProps}
                                sx={{ width: 80 }}
                              />
                            </Box>

                            {item.name === "Monitors" && (
                              <Box sx={{ pb: 2, pt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  Tell us a bit more about these monitors so we bring the right truck and supplies:
                                </Typography>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 2,
                                  }}
                                >
                                  <FormControl variant="standard" sx={{ width: "100%" }}>
                                    <InputLabel shrink>Sizes?</InputLabel>
                                    <AppSelect
                                      id="monitor-size"
                                      value={monitorDetails.size}
                                      onChange={(e) => handleMonitorDetailChange("size", e.target.value as string)}
                                    >
                                      {MONITOR_SIZE_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </AppSelect>
                                  </FormControl>
                                  <FormControl variant="standard" sx={{ width: "100%" }}>
                                    <InputLabel shrink>Attached to stands or arms?</InputLabel>
                                    <AppSelect
                                      id="monitor-stands"
                                      value={monitorDetails.attachedToStands}
                                      onChange={(e) =>
                                        handleMonitorDetailChange("attachedToStands", e.target.value as string)
                                      }
                                    >
                                      {MONITOR_STAND_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </AppSelect>
                                  </FormControl>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </AccordionDetails>
              </CategoryAccordion>
            );
          })}
        </Box>
      </Box>
    </form>
  );
}
